import * as React from "react";
import {api} from "../../../api/api";
import {EvChargingMeterPointTaken} from "../../../api/EvChargingApi";
import Modal from "../../ui/modal/Modal";
import ButtonPrimary from "../../../hiberUiKit/src/atoms/Buttons/ButtonPrimary";
import ButtonTertiary from "../../../hiberUiKit/src/atoms/Buttons/ButtonTertiary";
import {notifyBugsnagAndShowToast} from "../../../utils/Utils";
import {formatMprn, MPRN_LENGTH, mprnDigits} from "../evChargingBill";
import {evColors, evMessageStyles} from "../evChargingStyles";

interface Props {
	partnerBrandId: string;
	partnerBrandName: string;
	/**
	 * Called once a meter has been added here or moved here.
	 */
	onDone: () => void;
	onClose: () => void;
}

interface State {
	mprn: string;
	dedicated?: boolean;
	name: string;
	saving: boolean;
	error?: string;
	/**
	 * Set when a brand already holds the MPRN typed.
	 */
	taken?: EvChargingMeterPointTaken;
	confirmingMove: boolean;
}

/**
 * Adds a meter to the partner brand. An MPRN another brand holds can be moved here instead: that is how a meter follows
 * a building to its new owner.
 */
export default class AddEvChargingMeterModal extends React.Component<Props, State> {
	state: State = {mprn: "", name: "", saving: false, confirmingMove: false};

	private unmounted = false;

	componentWillUnmount() {
		this.unmounted = true;
	}

	render() {
		const {taken, confirmingMove} = this.state;
		return (
			<Modal onCloseClicked={this.props.onClose} title={confirmingMove ? "Move meter" : "Add meter"} autoWidth={true}>
				<div style={styles.body}>
					{confirmingMove && taken ? this.renderMove(taken) : this.renderForm()}
				</div>
			</Modal>
		);
	}

	private renderForm() {
		const {mprn, dedicated, name, saving, error, taken} = this.state;
		const heldHere = taken && taken.heldByPartnerBrandId === this.props.partnerBrandId;
		const heldElsewhere = taken && !heldHere;
		return (
			<>
				<div className={"m-form-group"}>
					<label className={"m-form-group_label"} htmlFor={"ev-add-mprn"}>MPRN</label>
					<input id={"ev-add-mprn"} className={"a-input"} inputMode={"numeric"} autoFocus value={formatMprn(mprn)}
						   placeholder={"1001 234 5678"} aria-invalid={!!error || !!taken}
						   style={{...styles.mprn, borderColor: error || taken ? evColors.error : undefined}}
						   onChange={e => this.setState({mprn: mprnDigits(e.target.value), error: undefined, taken: undefined})}/>
					<div aria-live={"polite"}>
						{error && <div style={evMessageStyles.error}>{error}</div>}
						{heldHere && <div style={evMessageStyles.error}>This brand already has this meter.</div>}
						{taken && heldElsewhere &&
						<div style={styles.taken}>
							<span>Held by <strong>{taken.heldByPartnerBrandName}</strong>.</span>
							<ButtonPrimary label={`Move to ${this.props.partnerBrandName}`}
										   onClick={() => this.setState({confirmingMove: true})}/>
						</div>}
					</div>
				</div>
				<fieldset className={"m-form-group"} style={styles.fieldset} disabled={!!taken}>
					<legend className={"m-form-group_label"}>EV charging only?</legend>
					<label style={styles.radio}>
						<input type={"radio"} name={"ev-add-dedicated"} checked={dedicated === true}
							   onChange={() => this.setState({dedicated: true})}/> Yes
					</label>
					<label style={styles.radio}>
						<input type={"radio"} name={"ev-add-dedicated"} checked={dedicated === false}
							   onChange={() => this.setState({dedicated: false})}/> No
					</label>
				</fieldset>
				<div className={"m-form-group"}>
					<label className={"m-form-group_label"} htmlFor={"ev-add-name"}>Name (optional)</label>
					<input id={"ev-add-name"} className={"a-input"} value={name} maxLength={100} disabled={!!taken}
						   placeholder={"e.g. Rooftop chargers"} onChange={e => this.setState({name: e.target.value})}/>
				</div>
				<div style={styles.buttons}>
					<ButtonTertiary label={"Cancel"} onClick={this.props.onClose}/>
					<ButtonPrimary label={"Add meter"} onClick={this.add} isLoading={saving} disabled={!this.canAdd()}/>
				</div>
			</>
		);
	}

	private renderMove(taken: EvChargingMeterPointTaken) {
		const {saving} = this.state;
		const from = <strong>{taken.heldByPartnerBrandName}</strong>;
		const to = <strong>{this.props.partnerBrandName}</strong>;
		return (
			<>
				<p>Move meter <strong style={styles.mprn}>{formatMprn(taken.mprn)}</strong> from {from} to {to}?</p>
				{/* bills entered so far stay where they are; only the ones still to come cannot reach the old brand */}
				<p style={{marginBottom: 0}}>
					After the move, new bills go to {to}, so enter any remaining bills for {from} first.
				</p>
				<div style={styles.buttons}>
					<ButtonTertiary label={"Back"} onClick={() => this.setState({confirmingMove: false})} disabled={saving}/>
					<ButtonPrimary label={"Move meter"} onClick={() => this.move(taken)} isLoading={saving} disabled={saving}/>
				</div>
			</>
		);
	}

	private canAdd(): boolean {
		const {mprn, dedicated, saving, taken} = this.state;
		return !saving && !taken && mprn.length === MPRN_LENGTH && dedicated !== undefined;
	}

	private add = () => {
		const {mprn, dedicated, name} = this.state;
		if (!this.canAdd()) return;
		this.setState({saving: true, error: undefined});
		api.instance.evChargingApi.addMeterPoint(this.props.partnerBrandId,
			{mprn, dedicatedToEvCharging: dedicated!, nickname: name.trim() || undefined})
			.then(() => !this.unmounted && this.props.onDone())
			.catch(error => {
				if (this.unmounted) return;
				const status = error && error.response && error.response.status;
				const data = error && error.response && error.response.data;
				if (status === 409 && data && data.meterPointId)
					this.setState({saving: false, taken: data});
				else if (status === 422 && data && data.message)
					this.setState({saving: false, error: data.message});
				else {
					this.setState({saving: false});
					notifyBugsnagAndShowToast(error, "Add EV charging meter");
				}
			});
	};

	private move(taken: EvChargingMeterPointTaken) {
		if (this.state.saving) return;
		this.setState({saving: true});
		api.instance.evChargingApi.moveMeterPoint(taken.meterPointId, this.props.partnerBrandId)
			.then(() => !this.unmounted && this.props.onDone())
			.catch(error => {
				if (this.unmounted) return;
				this.setState({saving: false});
				notifyBugsnagAndShowToast(error, "Move EV charging meter");
			});
	}
}

const styles: { [key: string]: React.CSSProperties } = {
	body: {width: 480, maxWidth: "100%"},
	mprn: {fontWeight: 600, fontVariantNumeric: "tabular-nums"},
	taken: {
		...evMessageStyles.warning, fontSize: 14, display: "flex", alignItems: "center", justifyContent: "space-between",
		gap: 12, padding: "8px 8px 8px 12px"
	},
	fieldset: {border: 0, padding: 0, margin: 0},
	radio: {display: "block", fontWeight: 400, margin: "6px 0", cursor: "pointer"},
	buttons: {display: "flex", justifyContent: "space-between", marginTop: 24}
};
